import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Clock, TrendingUp, TrendingDown, Minus, Trophy, Wallet, Shield, ArrowRightLeft, GitCompareArrows, Search, Award } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

/**
 * Iter 69: modal timeline multi-stagione di un giocatore.
 * Iter 70: doppia tab — Serie A (quotazioni ufficiali) + Fanta Club (carriera intra-lega).
 * Iter 71: modalità "Confronta" — affianca un secondo giocatore nello stesso modale.
 *
 * Props:
 *   - fantaId    : ID Serie A (serie_a_rooster / archive) — abilita tab "Serie A"
 *   - roosterId  : ID magnati_rooster                     — abilita tab "Fanta Club"
 *   - playerName : nome per l'header
 *   - defaultTab : "seriea" | "fanta" (opzionale)
 */
export default function PlayerTimelineDialog({ open, fantaId, roosterId, playerName, onClose, defaultTab }) {
  const hasSerieA = !!fantaId;
  const hasFanta = !!roosterId;

  const [tab, setTab] = useState(defaultTab || (hasSerieA ? "seriea" : "fanta"));
  const [serieaEntries, setSerieaEntries] = useState([]);
  const [fantaPlayer, setFantaPlayer] = useState(null);
  const [loadingSeriea, setLoadingSeriea] = useState(false);
  const [loadingFanta, setLoadingFanta] = useState(false);

  // Iter 71: confronto con un secondo giocatore
  const [comparePicker, setComparePicker] = useState(false);
  const [compareTarget, setCompareTarget] = useState(null); // { fantaId, roosterId, name }

  // reset compare quando si chiude il modale
  useEffect(() => {
    if (!open) {
      setCompareTarget(null);
      setComparePicker(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setTab(defaultTab || (hasSerieA ? "seriea" : "fanta"));
  }, [open, defaultTab, hasSerieA]);

  useEffect(() => {
    if (!open || !fantaId) return;
    setLoadingSeriea(true);
    api.get(`/rooster-history/by-fanta-id/${fantaId}`)
      .then(r => setSerieaEntries(r.data.entries || []))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoadingSeriea(false));
  }, [open, fantaId]);

  useEffect(() => {
    if (!open || !roosterId) return;
    setLoadingFanta(true);
    api.get(`/magnati/rooster/${roosterId}`)
      .then(r => setFantaPlayer(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoadingFanta(false));
  }, [open, roosterId]);

  if (!open) return null;

  const displayName = playerName
    || fantaPlayer?.canonical_name
    || serieaEntries[0]?.name
    || "—";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="player-timeline-dialog">
      <div onClick={e => e.stopPropagation()}
        className={`w-full ${compareTarget ? "max-w-5xl" : "max-w-3xl"} max-h-[85vh] overflow-hidden flex flex-col rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-purple-500/40 shadow-2xl shadow-purple-500/20 transition-all`}>
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-purple-500/20 border border-purple-500/40 flex items-center justify-center shrink-0">
              <Clock className="text-purple-300" size={22}/>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.3em] text-purple-400 uppercase font-bold">
                {compareTarget ? "Confronto Giocatori" : "Storia Giocatore"}
              </div>
              <h2 className="text-2xl font-black text-white mt-0.5 truncate" data-testid="player-timeline-name">
                {displayName}
                {compareTarget && (
                  <>
                    <span className="text-slate-500 font-normal mx-2">vs</span>
                    <span className="text-fuchsia-300">{compareTarget.name}</span>
                  </>
                )}
              </h2>
              <div className="text-xs text-slate-500 font-mono mt-0.5 truncate">
                {fantaId && <>fanta_id · {fantaId}</>}
                {fantaId && roosterId && <span className="mx-1">·</span>}
                {roosterId && <>club_id · {roosterId}</>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {compareTarget ? (
              <button
                onClick={() => { setCompareTarget(null); setComparePicker(false); }}
                data-testid="compare-clear-btn"
                title="Rimuovi confronto"
                className="text-fuchsia-400 hover:text-fuchsia-200 border border-fuchsia-500/30 hover:border-fuchsia-400 rounded-lg px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors inline-flex items-center gap-1"
              >
                <X size={12}/> Confronto
              </button>
            ) : (
              <button
                onClick={() => setComparePicker(v => !v)}
                data-testid="compare-open-btn"
                title="Confronta con un altro giocatore"
                className="text-fuchsia-400 hover:text-fuchsia-200 border border-fuchsia-500/30 hover:border-fuchsia-400 rounded-lg px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors inline-flex items-center gap-1"
              >
                <GitCompareArrows size={12}/> Confronta
              </button>
            )}
            <button onClick={onClose} className="text-slate-500 hover:text-white p-1" data-testid="player-timeline-close">
              <X size={20}/>
            </button>
          </div>
        </div>

        {/* Compare picker */}
        {comparePicker && !compareTarget && (
          <ComparePicker
            excludeFantaId={fantaId}
            excludeRoosterId={roosterId}
            onSelect={(pl) => { setCompareTarget(pl); setComparePicker(false); }}
            onClose={() => setComparePicker(false)}
          />
        )}

        {/* Tab bar (visibile solo se entrambi disponibili sul primo giocatore) */}
        {hasSerieA && hasFanta && (
          <div className="flex gap-1 px-5 pt-3 border-b border-slate-800 bg-slate-950/40">
            <TabBtn active={tab === "seriea"} onClick={() => setTab("seriea")} testid="tab-seriea">
              📈 Serie A
            </TabBtn>
            <TabBtn active={tab === "fanta"} onClick={() => setTab("fanta")} testid="tab-fanta">
              📋 Fanta Club
            </TabBtn>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {compareTarget ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <PlayerColumn
                title={displayName}
                accent="purple"
                tab={tab}
                fantaId={fantaId}
                roosterId={roosterId}
                serieaEntries={serieaEntries}
                serieaLoading={loadingSeriea}
                fantaPlayer={fantaPlayer}
                fantaLoading={loadingFanta}
              />
              <PlayerColumnRemote
                title={compareTarget.name}
                accent="fuchsia"
                tab={tab}
                fantaId={compareTarget.fantaId}
                roosterId={compareTarget.roosterId}
              />
            </div>
          ) : (
            <>
              {tab === "seriea" && hasSerieA && (
                <SerieATimeline entries={serieaEntries} loading={loadingSeriea}/>
              )}
              {tab === "fanta" && hasFanta && (
                <FantaClubTimeline player={fantaPlayer} loading={loadingFanta}/>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*                          TAB: SERIE A                              */
/* ------------------------------------------------------------------ */
function SerieATimeline({ entries, loading }) {
  const seasons = entries;
  const first = seasons[0];
  const last = seasons[seasons.length - 1];
  const qaTrend = first && last ? Math.round(last.qa || 0) - Math.round(first.qa || 0) : 0;
  const fvmTrend = first && last ? Math.round(last.fvm || 0) - Math.round(first.fvm || 0) : 0;

  if (loading) return <div className="text-center text-slate-500 py-10">Carico storia Serie A…</div>;
  if (seasons.length === 0) return <div className="text-center text-slate-500 py-10">Nessuna presenza storica trovata.</div>;

  return (
    <>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <MiniKPI label="Stagioni tracciate" v={seasons.length} accent="text-purple-300"/>
        <MiniKPI label="Trend Q.A." v={<TrendBadge value={qaTrend}/>} accent=""/>
        <MiniKPI label="Trend FVM" v={<TrendBadge value={fvmTrend}/>} accent=""/>
      </div>
      <div className="space-y-2">
        {seasons.map((s, i) => {
          const prev = i > 0 ? seasons[i - 1] : null;
          const sQa = Math.round(s.qa || 0);
          const sFvm = Math.round(s.fvm || 0);
          const sQi = Math.round(s.qi || 0);
          const sFvmM = Math.round(s.fvm_m || 0);
          const dqa = prev ? sQa - Math.round(prev.qa || 0) : 0;
          const dfvm = prev ? sFvm - Math.round(prev.fvm || 0) : 0;
          return (
            <div key={`${s.season}-${s.fanta_id}`}
              data-testid={`timeline-row-${s.season.replace("/", "-")}`}
              className="p-3 rounded-xl border border-slate-800 bg-slate-950/40 hover:border-purple-500/40 transition-colors">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-lg font-black font-mono text-white bg-purple-500/15 border border-purple-500/40 rounded-lg px-3 py-1 shrink-0">{s.season}</span>
                <div className="flex-1 min-w-[140px]">
                  <div className="font-bold text-white">{s.name}</div>
                  <div className="text-xs text-slate-400">🏟️ {s.real_team}</div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-bold uppercase">{s.role_classic}</span>
                  {s.roles_mantra?.length > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-300 font-black">
                      {s.roles_mantra.join("/")}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-3 text-right shrink-0">
                  <TimelineStat label="Q.I." v={sQi}/>
                  <TimelineStat label="Q.A." v={sQa} delta={dqa} showDelta={!!prev}/>
                  <TimelineStat label="FVM" v={sFvm} delta={dfvm} showDelta={!!prev}/>
                  <TimelineStat label="FVM M" v={sFvmM}/>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*                        TAB: FANTA CLUB                             */
/* ------------------------------------------------------------------ */
const BADGE_COLORS = {
  amber: "border-amber-500/40 bg-amber-500/10 text-amber-200",
  purple: "border-purple-500/40 bg-purple-500/10 text-purple-200",
  sky: "border-sky-500/40 bg-sky-500/10 text-sky-200",
  emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  rose: "border-rose-500/40 bg-rose-500/10 text-rose-200",
  slate: "border-slate-700 bg-slate-800/60 text-slate-300",
};

function FantaClubTimeline({ player, loading }) {
  const career = useMemo(
    () => (player?.career || []).slice().sort((a, b) => (a.season_num || 0) - (b.season_num || 0)),
    [player]
  );

  const transfers = useMemo(() => {
    const t = [];
    for (let i = 1; i < career.length; i++) {
      if (career[i - 1].club_id !== career[i].club_id) {
        t.push({
          season: career[i].season,
          from: career[i - 1].club_current_name || "?",
          to: career[i].club_current_name || "?",
          cost: career[i].acquisition_cost,
        });
      }
    }
    return t;
  }, [career]);

  const chartData = useMemo(
    () => career.map(c => ({
      season: c.season,
      qa: Number(c.final_value) || 0,
      costo: Number(c.acquisition_cost) || 0,
      stipendio: Number(c.salary) || 0,
    })),
    [career]
  );

  const records = player?.records || {};
  const badges = records.badges || [];
  const awards = player?.personal_awards || [];
  const uniqueClubs = records.unique_clubs || new Set(career.map(c => c.club_id)).size;
  const totalSeasons = records.total_seasons || career.length;

  if (loading) return <div className="text-center text-slate-500 py-10">Carico storia fanta club…</div>;
  if (!player) return <div className="text-center text-slate-500 py-10">Nessun dato fanta club per questo giocatore.</div>;
  if (career.length === 0) return <div className="text-center text-slate-500 py-10">Nessuna stagione fanta club registrata ancora.</div>;

  return (
    <div className="space-y-4">
      {/* Riepilogo KPI */}
      <div className="grid grid-cols-3 gap-2">
        <MiniKPI label="Stagioni fanta" v={totalSeasons} accent="text-amber-300"/>
        <MiniKPI label="Club diversi" v={uniqueClubs} accent="text-sky-300"/>
        <MiniKPI label="Trasferimenti" v={transfers.length} accent="text-rose-300"/>
      </div>

      {/* Record & Achievement Badges */}
      {badges.length > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-slate-900 to-slate-950 p-4" data-testid="fanta-records-card">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="text-amber-400 shrink-0" size={16}/>
            <div className="text-xs font-black uppercase tracking-widest text-amber-300">Record & Achievement</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {badges.map((b, i) => (
              <div key={i} data-testid={`fanta-badge-${i}`}
                className={`px-3 py-2 rounded-xl border ${BADGE_COLORS[b.color] || BADGE_COLORS.slate} flex items-start gap-2`}>
                <div className="text-xl leading-none pt-0.5 shrink-0">{b.emoji}</div>
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-wider truncate">{b.title}</div>
                  <div className="text-[10px] opacity-80 leading-snug">{b.detail}</div>
                </div>
              </div>
            ))}
          </div>
          {(records.highest_price || records.top_value) && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {records.highest_price && (
                <div className="p-2 rounded-lg bg-slate-950/60 border border-slate-800 text-slate-300">
                  💸 <b className="text-slate-100">Costo max</b>: {records.highest_price.cost} cr in {records.highest_price.season} · {records.highest_price.club_name || "?"}
                </div>
              )}
              {records.top_value && (
                <div className="p-2 rounded-lg bg-slate-950/60 border border-slate-800 text-slate-300">
                  💎 <b className="text-slate-100">Valore top</b>: {records.top_value.value} in {records.top_value.season} · {records.top_value.club_name || "?"}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Awards personali */}
      {awards.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3" data-testid="fanta-awards-card">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black flex items-center gap-1.5 mb-2">
            <Award size={12}/> Premi Individuali ({awards.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {awards.map((aw, i) => (
              <div key={i} className="px-2 py-1 rounded-full bg-slate-950/60 border border-amber-500/40 text-[11px]">
                <span className="text-amber-300 font-bold">{aw.award}</span>
                <span className="text-slate-500 ml-1.5 font-mono text-[10px]">{aw.season}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grafico Andamento — 2 assi: crediti fanta (dx) + Q.A. Serie A (sx) */}
      {chartData.length >= 2 && (
        <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3" data-testid="fanta-value-chart">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black flex items-center gap-1.5 mb-2">
            <TrendingUp size={12} className="text-amber-400"/> Andamento (crediti fanta vs Q.A. Serie A)
          </div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3"/>
                <XAxis dataKey="season" stroke="#64748b" fontSize={10}/>
                <YAxis yAxisId="cr" stroke="#a855f7" fontSize={10} label={{ value: "cr", angle: -90, position: "insideLeft", offset: 15, fill: "#a855f7", fontSize: 10 }}/>
                <YAxis yAxisId="qa" orientation="right" stroke="#f59e0b" fontSize={10} label={{ value: "Q.A.", angle: 90, position: "insideRight", offset: 10, fill: "#f59e0b", fontSize: 10 }}/>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }}/>
                <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }}/>
                <Line yAxisId="cr" type="monotone" dataKey="costo" stroke="#a855f7" strokeWidth={2} dot={{ r: 3 }} name="Costo (cr)"/>
                <Line yAxisId="cr" type="monotone" dataKey="stipendio" stroke="#22d3ee" strokeWidth={2} dot={{ r: 3 }} name="Stipendio (cr)"/>
                <Line yAxisId="qa" type="monotone" dataKey="qa" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4 }} name="Q.A. Serie A"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Career rows */}
      <div className="space-y-2">
        {career.map((c, i) => {
          const prev = i > 0 ? career[i - 1] : null;
          const isTransfer = prev && prev.club_id !== c.club_id;
          const cost = Number(c.acquisition_cost) || 0;
          const qaEnd = Number(c.final_value) || 0;
          const salary = Number(c.salary) || 0;
          return (
            <div key={`${c.season}-${c.club_id}-${i}`}
              data-testid={`fanta-row-${c.season.replace("/", "-")}`}
              className={`p-3 rounded-xl border ${isTransfer ? "border-rose-500/40 bg-rose-500/5" : "border-slate-800 bg-slate-950/40"} hover:border-amber-500/40 transition-colors`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-lg font-black font-mono text-white bg-amber-500/15 border border-amber-500/40 rounded-lg px-3 py-1 shrink-0">{c.season}</span>
                <div className="flex-1 min-w-[140px]">
                  <div className="font-bold text-white flex items-center gap-1.5 flex-wrap">
                    {c.club_current_name || "?"}
                    {isTransfer && (
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-rose-500/15 border border-rose-500/30 text-rose-300 font-bold inline-flex items-center gap-1">
                        <ArrowRightLeft size={9}/> da {prev.club_current_name}
                      </span>
                    )}
                    {c.blindato && (
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-500/30 text-sky-300 font-bold inline-flex items-center gap-1">
                        <Shield size={9}/> Blindato
                      </span>
                    )}
                  </div>
                  {c.ruolo && <div className="text-xs text-slate-400 uppercase mt-0.5">{c.ruolo}</div>}
                </div>
                <div className="grid grid-cols-3 gap-4 text-right shrink-0">
                  <TimelineStat label="Costo cr" v={cost}/>
                  <TimelineStat
                    label="Stipendio"
                    v={<span className="inline-flex items-center gap-0.5"><Wallet size={9}/>{salary}</span>}
                  />
                  <TimelineStat label="Q.A. fine" v={qaEnd}/>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-[10px] text-slate-500 leading-relaxed" data-testid="fanta-legend">
        <b className="text-slate-400">Legenda</b>: <b>Costo cr</b> = crediti fanta pagati all&apos;acquisto · <b>Stipendio</b> = crediti/anno · <b>Q.A. fine</b> = quotazione Serie A a fine stagione (Fantacalcio.it). Sono grandezze diverse, non usarle in un rapporto diretto.
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*                             SHARED                                 */
/* ------------------------------------------------------------------ */
function TabBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-xs font-black uppercase tracking-widest rounded-t-lg transition-colors ${
        active
          ? "bg-slate-900 text-white border border-b-0 border-purple-500/40"
          : "text-slate-500 hover:text-slate-300 border border-transparent"
      }`}>
      {children}
    </button>
  );
}

function MiniKPI({ label, v, accent }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className={`text-xl font-black font-mono mt-1 ${accent}`}>{v}</div>
    </div>
  );
}

function TimelineStat({ label, v, delta = 0, showDelta = false }) {
  const dColor = delta > 0 ? "text-emerald-400" : delta < 0 ? "text-rose-400" : "text-slate-500";
  const DIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="text-sm font-mono font-black text-white flex items-center gap-1 justify-end">
        {v}
        {showDelta && delta !== 0 && (
          <span className={`text-[9px] font-black flex items-center gap-0.5 ${dColor}`}>
            <DIcon size={9}/>{delta > 0 ? "+" : ""}{delta}
          </span>
        )}
      </div>
    </div>
  );
}

function TrendBadge({ value }) {
  if (!value) return <span className="text-slate-500 text-sm">—</span>;
  const positive = value > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const cls = positive ? "text-emerald-400" : "text-rose-400";
  return (
    <span className={`inline-flex items-center gap-1 ${cls}`}>
      <Icon size={16}/> {positive ? "+" : ""}{value}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*             ITER 71: CONFRONTO — Picker + Colonne                  */
/* ------------------------------------------------------------------ */
function ComparePicker({ excludeFantaId, excludeRoosterId, onSelect, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/rooster/search-all", { params: { q: q.trim(), limit: 15 } });
        setResults((data.results || []).filter(r => r.fanta_id !== excludeFantaId));
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, excludeFantaId]);

  async function handlePick(r) {
    // Prova a risolvere il rooster_id (magnati) tramite resolve-batch by name
    let roosterId = null;
    try {
      const rb = await api.post("/magnati/rooster/resolve-batch", { names: [r.name] });
      roosterId = rb.data?.map?.[r.name] || null;
      if (roosterId && roosterId === excludeRoosterId) roosterId = null;
    } catch { /* non blocca */ }
    onSelect({ fantaId: r.fanta_id, roosterId, name: r.name });
  }

  return (
    <div className="border-b border-fuchsia-500/30 bg-fuchsia-500/5 p-4 space-y-2" data-testid="compare-picker">
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca un giocatore da confrontare (min. 2 lettere)…"
            data-testid="compare-search-input"
            className="w-full bg-slate-950 border border-fuchsia-500/40 rounded-md pl-9 pr-3 py-2 text-sm focus:border-fuchsia-400 outline-none"
          />
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-slate-500 hover:text-slate-200 text-xs px-2 py-1"
        >
          Annulla
        </button>
      </div>
      {loading && <div className="text-xs text-slate-500 px-1">Cerco…</div>}
      {!loading && results.length > 0 && (
        <ul className="max-h-56 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/60 divide-y divide-slate-800">
          {results.map((r) => (
            <li key={r.fanta_id}>
              <button
                type="button"
                onClick={() => handlePick(r)}
                data-testid={`compare-pick-${r.fanta_id}`}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-fuchsia-500/10 text-left"
              >
                <span className="text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 shrink-0">
                  {r.role_classic || "?"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white truncate">{r.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {r.real_team} · Q.A. {r.qa} · {r.source === "current" ? "26/27" : r.season}
                  </div>
                </div>
                <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded shrink-0 ${
                  r.source === "current" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-slate-700/40 text-slate-400 border border-slate-600"
                }`}>
                  {r.source === "current" ? "attivo" : "storico"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!loading && q.trim().length >= 2 && results.length === 0 && (
        <div className="text-xs text-slate-500 px-1">Nessun giocatore trovato</div>
      )}
    </div>
  );
}

function PlayerColumn({ title, accent, tab, fantaId, roosterId, serieaEntries, serieaLoading, fantaPlayer, fantaLoading }) {
  const accentCls = accent === "fuchsia" ? "text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/5" : "text-purple-300 border-purple-500/40 bg-purple-500/5";
  return (
    <div className={`rounded-2xl border ${accentCls} p-3`}>
      <div className={`text-xs uppercase tracking-widest font-black ${accent === "fuchsia" ? "text-fuchsia-300" : "text-purple-300"} pb-2 mb-3 border-b border-slate-800 truncate`}>
        {title}
      </div>
      {tab === "seriea" && (
        fantaId
          ? <SerieATimeline entries={serieaEntries} loading={serieaLoading}/>
          : <div className="text-center text-slate-500 py-6 text-sm">Nessun dato Serie A</div>
      )}
      {tab === "fanta" && (
        roosterId
          ? <FantaClubTimeline player={fantaPlayer} loading={fantaLoading}/>
          : <div className="text-center text-slate-500 py-6 text-sm">Nessun dato Fanta Club</div>
      )}
    </div>
  );
}

// Wrapper che fetcha i dati del giocatore remoto (secondo giocatore del confronto)
function PlayerColumnRemote({ title, accent, tab, fantaId, roosterId }) {
  const [serieaEntries, setSerieaEntries] = useState([]);
  const [fantaPlayer, setFantaPlayer] = useState(null);
  const [loadingSeriea, setLoadingSeriea] = useState(false);
  const [loadingFanta, setLoadingFanta] = useState(false);

  useEffect(() => {
    if (!fantaId) { setSerieaEntries([]); return; }
    setLoadingSeriea(true);
    api.get(`/rooster-history/by-fanta-id/${fantaId}`)
      .then(r => setSerieaEntries(r.data.entries || []))
      .catch(() => setSerieaEntries([]))
      .finally(() => setLoadingSeriea(false));
  }, [fantaId]);

  useEffect(() => {
    if (!roosterId) { setFantaPlayer(null); return; }
    setLoadingFanta(true);
    api.get(`/magnati/rooster/${roosterId}`)
      .then(r => setFantaPlayer(r.data))
      .catch(() => setFantaPlayer(null))
      .finally(() => setLoadingFanta(false));
  }, [roosterId]);

  return (
    <PlayerColumn
      title={title}
      accent={accent}
      tab={tab}
      fantaId={fantaId}
      roosterId={roosterId}
      serieaEntries={serieaEntries}
      serieaLoading={loadingSeriea}
      fantaPlayer={fantaPlayer}
      fantaLoading={loadingFanta}
    />
  );
}
